import { types, applySnapshot } from "mobx-state-tree";

import CONSTANTS from "const/constants";
import { hasChanged } from "util/mst-utils";

import BaseBackboneModelMigrationStore from "../backbone-model-template";
import { UserCredentialsStore } from "../usercredentials/user-credentials-store";

const { USER_ROLES, USER_STATUS } = CONSTANTS;

const model = {
    userId: types.optional(types.identifier, ``),
    organizationId: types.maybeNull(types.string),
    accountType: types.maybeNull(types.string),
    status: types.maybeNull(types.string),
    isMfaEnabled: types.optional(types.boolean, false, [null, undefined]),
    cognitoUser: types.optional(types.boolean, false, [null, undefined]),
    cognitoUserPassEnabled: types.optional(types.boolean, false, [
        null,
        undefined
    ]),
    mfaStatus: types.optional(types.string, USER_STATUS.INACTIVE, [
        null,
        undefined
    ]),
    email: types.optional(types.string, ``),
    username: types.maybeNull(types.string),
    name: types.optional(types.string, ``),
    profileImageUrl: types.maybeNull(types.string),
    defaultCredential: types.maybeNull(types.string),
    roles: types.optional(types.array(types.string), () => [USER_ROLES.USER]),
    lastLoggedIn: types.maybeNull(types.integer),
    loginAttempts: types.maybeNull(types.integer),
    lastAttemptedLogin: types.maybeNull(types.integer),
    creationDate: types.maybeNull(types.integer),
    udvrChunkMonitoringEnabled: types.maybeNull(types.string),
    organization: types.maybeNull(types.frozen({})),
    socialCredentials: types.optional(UserCredentialsStore, {
        socialCredentials: []
    })
};

const volatile = () => ({
    identifierAttribute: `userId`,
    connected: false
});

const views = (self) => ({
    get id() {
        return self.userId;
    },

    get role() {
        return self.roles[0];
    },

    get isAdmin() {
        return self.role === USER_ROLES.ADMIN;
    },

    get isLoggedInUser() {
        return self.userId === App.Models.user.id;
    },

    get isActive() {
        return self.status === USER_STATUS.ACTIVE;
    },

    get isPending() {
        return self.status === USER_STATUS.PENDING_INVITE;
    },

    get isSuspended() {
        return self.status === USER_STATUS.DEACTIVATED;
    },

    get isDeleted() {
        return self.status === USER_STATUS.DELETED;
    },

    get isLocked() {
        return self.status === USER_STATUS.LOCKED;
    },

    get organizationCredentials() {
        console.log(self.socialCredentials);
        return self.socialCredentials.filter(
            (credential) => !!credential?.organizationCredentialId
        );
    },

    hasRole(role: string) {
        return role.toUpperCase() === self.role.toUpperCase;
    },

    toFormFormat() {
        return {
            id: self.userId,
            userId: self.userId,
            isAdmin: self.isAdmin,
            name: self.name,
            email: self.email,
            role: self.role,
            campaigns: [],
            streams: [],
            integrations: [],
            uploadedProfileImage: self.profileImageUrl,
            mfaStatus: self.mfaStatus,
            isMfaEnabled: self.isMfaEnabled,
            cognitoUser: self.cognitoUser,
            cognitoUserPassEnabled: self.cognitoUserPassEnabled
        };
    }
});

const actions = (self) => {
    return {
        /**
         * @summary Update model with new data (if changed)
         * @param {UserObject} data
         */
        update(data) {
            if (Object.hasOwn(data, `userId`)) {
                hasChanged(self, data) && applySnapshot(self, data);
            }
        },

        setSocialCredentials(socialCredentials) {
            self.socialCredentials = socialCredentials;
        }
    };
};

const _UserStore = types
    .model(`User`, model)
    .volatile(volatile)
    .views(views)
    .actions(actions);

export const UserStore =
    BaseBackboneModelMigrationStore<typeof _UserStore>(_UserStore);
